import { CircularProgress } from "@/components/ui/circular-progress";
import { ITEM_PLACEHOLDER_IMAGE } from "@/components/ui/item-image-box-sizes";
import { ItemPicture } from "@/components/ui/item-picture";
import { ItemPlaceholderMark } from "@/components/ui/item-placeholder-mark";
import { MuiTypography } from "@/components/ui/mui-typography";
import { useItemImageFailure } from "@/hooks/use-item-image-failure";
import {
  itemImageAlt,
  itemImageSources,
  type CatalogItemImageInput,
} from "@/lib/catalog-item-image";
import { cn } from "@/lib/utils";

// Two pieces the donor's two **compact** item cards share — the Add Variant
// dialog's preview card (`AddVariantIsland/components/item-details.tsx`) and the
// Item Search island's card (`ItemSearchIsland/components/ItemCard/
// ItemCard.tsx`, #798). The island card is a copy of the preview card in the
// donor, block for block: a 250px hero, the 28px variant strip, the short-label
// fact lines. So the two Atlas ports draw these from one place, and each
// passes the argument its donor differs on.
//
// Not the catalog card's pieces: that card is a different donor
// (`items-card-list/item-card.tsx`) with a 32px strip of `ItemImageBox` tiles
// and spelled-out field labels with `N/A` (`item-fields.tsx`).

/** One tile of a compact card's strip — a group member and its picture. */
export interface VariantThumb {
  id: number;
  heroImage?: CatalogItemImageInput | null;
  /** The member's name when the caller has it, for the picture's alt. */
  name?: string;
}

interface VariantThumbStripProps {
  /** The tiles to draw, already ordered (and capped) by the caller. */
  thumbs: readonly VariantThumb[];
  /** The tile drawn current — the variant on show. */
  currentId: number;
  onSelect: (itemId: number) => void;
  /**
   * The donor's two arrangements:
   *
   * - `grid` — the Add Variant preview's `display: grid` box,
   *   `gridTemplateColumns: 'repeat(8, 1fr)'` at a 4px gap, so a ninth tile
   *   wraps and the tiles never stretch (`item-details.tsx:319-347`);
   * - `row` — the island's `Stack direction='row' spacing={0.6}` (4.8px) beside
   *   a right-hand `+N variants` count (`VariantTiles.tsx`), with a spinner on
   *   the current tile while its read is in flight.
   */
  arrangement: "grid" | "row";
  /** `row` only: the tile whose read is in flight. */
  loadingId?: number;
  /** `row` only: how many members the cap left out. */
  moreCount?: number;
  /**
   * `row` only: a press began on the strip — the island's
   * `onMouseDownCapture`, which launches its read ahead of the click. A tile's
   * Enter or Space counts too, since the tiles here are buttons (#254 D2) and a
   * keyboard press has no pointer-down.
   */
  onPressStart?: () => void;
  /** The prefix of the strip's and its tiles' test ids. */
  testIdPrefix: string;
}

/**
 * The compact cards' 28px variant strip.
 *
 * Against the catalog card's strip (`item-variant-tiles.tsx`): 28px inside a 1px
 * border with 1px of padding rather than a 32px `ItemImageBox`; `background-size:
 * cover` rather than `object-fit: contain`; and **black** for the current tile
 * rather than `primary.main`.
 *
 * Its tiles are **buttons, where the donor's are not** — an owner-ruled parity
 * break (#254 decision menu, D2 = b) for the preview's strip, carried to the
 * island's (#798's decision round). The donor draws a `Box` with an `onClick`,
 * so its tiles carry no role, no `aria-pressed` and no keyboard reach at all.
 * The shape is the catalog card's strip's, which *is* buttons in both
 * codebases, so the strips in this app answer the keyboard the same way.
 */
export function VariantThumbStrip({
  thumbs,
  currentId,
  onSelect,
  arrangement,
  loadingId,
  moreCount = 0,
  onPressStart,
  testIdPrefix,
}: VariantThumbStripProps) {
  if (thumbs.length === 0) return null;

  const tiles = thumbs.map((thumb) => (
    <div
      key={thumb.id}
      data-slot="preview-variant-tile"
      data-variant-id={thumb.id}
      className={cn(
        "rounded-[4px] border p-px",
        thumb.id === currentId ? "border-black" : "border-lennar-preview-tile-border",
      )}
    >
      {/* The name is the donor's own label text, id and all: a tile's picture
          says nothing a screen-reader user can act on. */}
      <button
        type="button"
        data-testid={`${testIdPrefix}-tile-${thumb.id}`}
        aria-label={`Show variant ${thumb.id}`}
        aria-pressed={thumb.id === currentId}
        onClick={() => onSelect(thumb.id)}
        // `cursor-pointer` unconditionally, where the preview's donor writes
        // `cursor: isSuccessLoadVariants ? 'pointer' : 'default'`
        // (`item-details.tsx:342`). That flag is a *second* detail fetch the
        // donor's dialog fires for itself, and Atlas's tiles arrive with
        // switchability already in hand, so the `default` arm has no reachable
        // state here. The island's donor is `pointer` outright.
        //
        // The focus ring is the added affordance's other half. Still no ripple
        // and no hover treatment: the donor's tile is not a `ButtonBase`.
        // `block`: the donor's tile is a `Box`. An inline-level button sits on
        // its frame's line box, which adds the descender space under it — a
        // 32×38 frame where the donor's is 32 square.
        className={cn(
          "block size-7 cursor-pointer overflow-hidden rounded-[4px] leading-[0]",
          "focus-visible:outline-lennar-blue-600 focus-visible:outline-2 focus-visible:outline-offset-2",
          arrangement === "row" && "relative",
        )}
      >
        {/* The donor sets the hero as a `background-image` at `cover`. An
            `img` instead, so the picture has an alt and the rendition ladder
            has a `srcset` to choose from — #269's ruled replacement for the
            donor's `?w=256` resize parameter. `object-cover` keeps the donor's
            `background-size: cover`, and `object-left-top` its crop *origin*:
            a background image with no `background-position` is drawn from the
            top-left, where an `img`'s `object-position` defaults to the
            centre. */}
        <VariantThumbImage
          thumb={thumb}
          // The island centres its no-image placeholder
          // (`backgroundPosition: heroImgUrl ? undefined : 'center'`,
          // `VariantTiles.tsx`); the preview leaves it at the top-left.
          placeholderPosition={arrangement === "row" ? "center" : "top-left"}
        />
        {arrangement === "row" && thumb.id === loadingId && (
          // `CircularProgress size={24}` at `top: 2, left: 2` inside the 28px
          // box — on the current tile, which is the one being read.
          <CircularProgress
            size={24}
            data-slot="variant-thumb-spinner"
            className="absolute top-0.5 left-0.5"
          />
        )}
      </button>
    </div>
  ));

  if (arrangement === "row") {
    return (
      // `Stack direction='row' justifyContent='space-between'
      // alignItems='center' sx={{ mt: 1 }}`.
      <div
        data-testid={`${testIdPrefix}-tiles`}
        className="mt-2 flex w-full items-center justify-between"
        onMouseDownCapture={onPressStart}
        onKeyDownCapture={(event) => {
          if (event.key === "Enter" || event.key === " ") onPressStart?.();
        }}
      >
        <div className="flex items-center gap-[4.8px]">{tiles}</div>
        {moreCount > 0 && (
          <MuiTypography variant="body2" color="secondary">
            {`+${moreCount} variant${moreCount > 1 ? "s" : ""}`}
          </MuiTypography>
        )}
      </div>
    );
  }

  return (
    // `Stack ... sx={{ mt: 1 }}` around a `display: grid` box.
    <div className="mt-2 flex w-full items-center justify-between">
      <div data-testid={`${testIdPrefix}-tiles`} className="grid grid-cols-8 gap-1">
        {tiles}
      </div>
    </div>
  );
}

/**
 * One tile's picture, or the donor's generic placeholder panel.
 *
 * `placeholder-image.png` rather than the house illustration — the donor uses a
 * different asset here from the one its cards substitute, and the two are kept
 * apart (`ITEM_PLACEHOLDER_IMAGE`).
 *
 * **The donor draws "no image" and "failed image" differently, so this does
 * too** (#496). Its tile is a CSS background: the placeholder asset when there
 * is no hero, and the hero's URL otherwise — which, when the object is missing,
 * the resize CDN answers with its own full-bleed glyph. So a *no-image* tile
 * shows the asset at its placement, while a *failed* tile shows the mark
 * centred. Atlas serves its own objects, where a missing one really does fail,
 * so without the failed arm the 28px tile drew Chromium's broken-image glyph.
 *
 * **The picture is `ItemPicture`** (#539), so it inherits the pre-hydration
 * re-arm every other slot gets.
 */
function VariantThumbImage({
  thumb,
  placeholderPosition,
}: {
  thumb: VariantThumb;
  placeholderPosition: "top-left" | "center";
}) {
  const image = thumb.heroImage;
  const sources = itemImageSources(image);
  // Keyed on the URLs, so the verdict belongs to the bytes this tile asked for
  // and a tile pointed at a different image retries.
  const [failed, noteFailed] = useItemImageFailure(sources);

  if (!image) {
    return (
      <img
        src={ITEM_PLACEHOLDER_IMAGE}
        // Decorative: the tile's button already carries the name, and the donor
        // draws this as a background with no alternative text at all.
        alt=""
        aria-hidden="true"
        className={cn(
          "size-7 object-cover",
          placeholderPosition === "center" ? "object-center" : "object-left-top",
        )}
      />
    );
  }

  if (failed) {
    return <ItemPlaceholderMark className="size-7" />;
  }

  return (
    <ItemPicture
      sources={sources}
      // The island's strip knows only ids and pictures — the list response's
      // group members — so its picture is decorative beside the button's name.
      alt={thumb.name === undefined ? "" : itemImageAlt(thumb.name)}
      // 28px, so the ladder's smallest rung is the right one — without a
      // `sizes` the browser assumes `100vw` and pulls the widest.
      sizes="28px"
      className="size-7 object-cover object-left-top"
      onError={noteFailed}
    />
  );
}

/**
 * One of a compact card's four facts under the name — `Category`, `Model #`,
 * `Item #`, `Color`.
 *
 * **The element is unconditional and the text is not**, which is the donor's
 * `{modelNumber && \`Model #: ${modelNumber}\`}` inside the `Typography` rather
 * than around it (`item-details.tsx:216-231`, `ItemCard.tsx:356-367`). So an
 * item missing a fact keeps its line's top margin and loses only the words — no
 * `N/A`, and no row shifting up.
 *
 * `body2` at the donor's `lineHeight: 1.1` override, in secondary text. The
 * margin is MUI's `mt: { xs: 0, sm: 1 }`, and `sm:` here is 600px because
 * `index.css` sets the app's whole scale to the donor's (#254's D16).
 */
export function ItemShortFactLine({
  testId,
  label,
  value,
}: {
  testId: string;
  label: string;
  value: string | null | undefined;
}) {
  return (
    <MuiTypography
      variant="body2"
      color="secondary"
      data-testid={testId}
      className="mt-0 leading-[1.1] sm:mt-2"
    >
      {value?.trim() ? `${label}: ${value}` : ""}
    </MuiTypography>
  );
}
